import { ReactNode } from 'react';

import { useIntl } from 'react-intl';

import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import Tooltip from '@atlaskit/tooltip';

import { messages } from 'src/app/sidebar/sidebar.i18n';
import Sidebar, {
  CollapsedContentProps,
  SidebarContent,
  ResizeProps,
} from 'src/components/sidebar';

import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_MIN_EXPANDED_WIDTH,
  SIDEBAR_MAX_EXPANDED_WIDTH,
} from '../constants';

import * as styles from './sidebar.style';

export type SidebarProps = {
  backgroundColor?: string;
  borderLeft?: string;
  expandedWidth: number;
  contentOnly?: boolean;
  collapsedContent: SidebarContent[];
  expandedContent: SidebarContent[];
  isCollapsed: boolean;
  onResize: (props: ResizeProps) => void;
  tabs?: ReactNode;
  onCollapsedContentClick: (props: CollapsedContentProps) => void;
  isBannerOpen: boolean;
  isCollapsible?: boolean;
  sidebarMinWidth?: number;
  children?: ReactNode | ReactNode[];
};

function GlobalSidebar({
  backgroundColor,
  borderLeft,
  isBannerOpen,
  contentOnly = false,
  isCollapsible = true,
  sidebarMinWidth = SIDEBAR_MIN_EXPANDED_WIDTH,
  children,
  ...props
}: SidebarProps): JSX.Element {
  const intl = useIntl();

  const icon = (
    <Tooltip
      position="left"
      content={intl.formatMessage(messages.expandIconTooltip)}
    >
      <PanelRightIcon
        spacing="spacious"
        label={intl.formatMessage(messages.expandIconTooltip)}
      />
    </Tooltip>
  );

  return (
    <styles.Container
      data-qa="sidebar-container"
      data-testid="sidebar-container"
      topOffset={0}
      borderLeft={borderLeft}
    >
      <Sidebar
        ariaLabel={intl.formatMessage(messages.sidebarLabel)}
        backgroundColor={backgroundColor}
        contentOnly={contentOnly}
        collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
        icon={icon}
        isCollapsible={isCollapsible}
        minExpandedWidth={sidebarMinWidth}
        maxExpandedWidth={SIDEBAR_MAX_EXPANDED_WIDTH}
        resizable
        toggleButtonCollapseLabel={intl.formatMessage(
          messages.toggleButtonCollapseLabel
        )}
        toggleButtonExpandLabel={intl.formatMessage(
          messages.toggleButtonExpandLabel
        )}
        {...props}
      >
        {children}
      </Sidebar>
    </styles.Container>
  );
}

// eslint-disable-next-line import/no-default-export
export default GlobalSidebar;
